import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search, ClipboardCheck, CheckCircle2, Circle, Calendar, CalendarDays, Music, ExternalLink, Target, Trophy, Copy, Download, Ban, MoreHorizontal, ChevronLeft, ChevronRight, List } from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { formatDateTimeShort, formatTime } from '@/lib/datetime';
import ClassCalendar from '@/components/ClassCalendar';

export default function OwnerClasses() {
    const today = new Date().toISOString().slice(0, 10);

    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState(today);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [activeTab, setActiveTab] = useState('list'); // 'list' | 'calendar'
    const DAYS = [
        { key: 'lunes', label: 'Lunes' },
        { key: 'martes', label: 'Martes' },
        { key: 'miercoles', label: 'Miércoles' },
        { key: 'jueves', label: 'Jueves' },
        { key: 'viernes', label: 'Viernes' },
        { key: 'sabado', label: 'Sábado' },
        { key: 'domingo', label: 'Domingo' },
    ];

    const emptySchedule = () => DAYS.reduce((acc, d) => ({ ...acc, [d.key]: [] }), {});

    const DURATIONS = [
        { value: '0.5', label: '30 min' },
        { value: '1', label: '1h' },
        { value: '1.5', label: '1,5h' },
        { value: '2', label: '2h' },
        { value: '2.5', label: '2,5h' },
        { value: '3', label: '3h' },
        { value: '4', label: '4h' },
    ];

    const calcEndDateTime = (date, time, duration) => {
        if (!date || !time || !duration) return '';
        const dt = new Date(`${date}T${time}`);
        dt.setMinutes(dt.getMinutes() + parseFloat(duration) * 60);
        const pad = n => String(n).padStart(2, '0');
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };

    const [form, setForm] = useState({ name: '', type: 'variable', start_date: '', start_time: '', duration: '1', max_capacity: '', schedule: emptySchedule(), copy_days: [] });

    const addSlot = (dayKey) => {
        setForm(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [dayKey]: [...prev.schedule[dayKey], { start: '09:00', end: '10:00' }],
            },
        }));
    };

    const removeSlot = (dayKey, idx) => {
        setForm(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [dayKey]: prev.schedule[dayKey].filter((_, i) => i !== idx),
            },
        }));
    };

    const updateSlot = (dayKey, idx, field, value) => {
        setForm(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [dayKey]: prev.schedule[dayKey].map((slot, i) => i === idx ? { ...slot, [field]: value } : slot),
            },
        }));
    };
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [attendanceOpen, setAttendanceOpen] = useState(false);
    const [attendanceClass, setAttendanceClass] = useState(null);
    const [attendances, setAttendances] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [songsOpen, setSongsOpen] = useState(false);
    const [songsClass, setSongsClass] = useState(null);
    const [songs, setSongs] = useState([]);
    const [songsLoading, setSongsLoading] = useState(false);
    const [songsCopied, setSongsCopied] = useState(false);

    // Goals state
    const [goalsOpen, setGoalsOpen] = useState(false);
    const [goalsClass, setGoalsClass] = useState(null);
    const [goals, setGoals] = useState([]);
    const [goalsLoading, setGoalsLoading] = useState(false);
    const [goalForm, setGoalForm] = useState({ title: '', description: '', target_value: '', unit: '', points: '' });
    const [goalSaving, setGoalSaving] = useState(false);
    const [goalError, setGoalError] = useState('');
    const [editingGoal, setEditingGoal] = useState(null);

    // Ranking state
    const [rankingOpen, setRankingOpen] = useState(false);
    const [rankingClass, setRankingClass] = useState(null);
    const [ranking, setRanking] = useState([]);
    const [rankingLoading, setRankingLoading] = useState(false);

    // Cancel state
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelClass, setCancelClass] = useState(null);
    const [cancelMessage, setCancelMessage] = useState('');
    const [cancelling, setCancelling] = useState(false);

    // Copy week/month state
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyForm, setCopyForm] = useState({ source_date: today, mode: 'week', copies: 1, copy_goals: false });
    const [copyLoading, setCopyLoading] = useState(false);
    const [copyError, setCopyError] = useState('');
    const [copyPreviewClasses, setCopyPreviewClasses] = useState([]);
    const [copyPreviewLoading, setCopyPreviewLoading] = useState(false);
    const [copySelectedIds, setCopySelectedIds] = useState([]);
    const [copyCreatedIds, setCopyCreatedIds] = useState([]);
    const [copyUndoLoading, setCopyUndoLoading] = useState(false);
    const [copySuccess, setCopySuccess] = useState(null);

    const fetchClasses = useCallback(async () => {
        setLoading(true);
        try {
            const params = { search, page, per_page: 10 };
            if (dateFilter) params.date = dateFilter;
            const res = await api.get('/owner/classes', { params });
            setClasses(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, dateFilter, page]);

    useEffect(() => { fetchClasses(); }, [fetchClasses]);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: '', start_date: today, start_time: '', duration: '1', max_capacity: '', schedule: emptySchedule(), copy_days: [] });
        setError('');
        setDialogOpen(true);
    };

    const parseScheduleFromApi = (apiSchedule) => {
        const s = emptySchedule();
        if (apiSchedule && typeof apiSchedule === 'object') {
            Object.entries(apiSchedule).forEach(([day, times]) => {
                if (s[day] !== undefined) {
                    const arr = Array.isArray(times) ? times : [times];
                    s[day] = arr.map(t => {
                        const parts = String(t).split('-');
                        return { start: parts[0] || '09:00', end: parts[1] || '10:00' };
                    });
                }
            });
        }
        return s;
    };

    const scheduleToApi = (schedule) => {
        const result = {};
        Object.entries(schedule).forEach(([day, slots]) => {
            if (slots.length > 0) {
                result[day] = slots.map(s => `${s.start}-${s.end}`);
            }
        });
        return Object.keys(result).length > 0 ? result : null;
    };

    const openEdit = (cls) => {
        setEditing(cls);
        let start_date = '', start_time = '', duration = '1';
        if (cls.starts_at) {
            start_date = cls.starts_at.slice(0, 10);
            start_time = cls.starts_at.slice(11, 16);
        }
        if (cls.starts_at && cls.ends_at) {
            const diffMs = new Date(cls.ends_at) - new Date(cls.starts_at);
            const diffH = diffMs / (1000 * 60 * 60);
            const closest = DURATIONS.reduce((prev, cur) => Math.abs(parseFloat(cur.value) - diffH) < Math.abs(parseFloat(prev.value) - diffH) ? cur : prev);
            duration = closest.value;
        }
        setForm({
            name: cls.name || '',
            start_date,
            start_time,
            duration,
            max_capacity: cls.max_capacity || '',
            schedule: parseScheduleFromApi(cls.schedule),
            copy_days: [],
        });
        setError('');
        setDialogOpen(true);
    };

    const getNextDaysOptions = () => {
        const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const options = [];
        for (let i = 1; i <= 6; i++) {
            const d = new Date();
            d.setDate(d.getDate() + i);
            options.push({
                value: d.toISOString().slice(0, 10),
                label: `${days[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`,
            });
        }
        return options;
    };

    const toggleCopyDay = (dateValue) => {
        setForm(prev => ({
            ...prev,
            copy_days: prev.copy_days.includes(dateValue)
                ? prev.copy_days.filter(d => d !== dateValue)
                : [...prev.copy_days, dateValue],
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const starts_at = form.start_date && form.start_time ? `${form.start_date}T${form.start_time}` : null;
            const ends_at = starts_at ? calcEndDateTime(form.start_date, form.start_time, form.duration) : null;

            if (editing) {
                // Edit mode - update single class
                const data = {
                    name: form.name,
                    starts_at,
                    ends_at,
                    max_capacity: form.max_capacity ? parseInt(form.max_capacity) : null,
                };
                await api.put(`/owner/classes/${editing.id}`, data);
            } else {
                // Create mode - create individual variable classes for selected days
                const datesToCreate = [form.start_date, ...form.copy_days];
                const uniqueDates = [...new Set(datesToCreate)].filter(d => d);

                for (const date of uniqueDates) {
                    const classStartsAt = form.start_time ? `${date}T${form.start_time}` : null;
                    const classEndsAt = classStartsAt ? calcEndDateTime(date, form.start_time, form.duration) : null;
                    const data = {
                        name: form.name,
                        starts_at: classStartsAt,
                        ends_at: classEndsAt,
                        max_capacity: form.max_capacity ? parseInt(form.max_capacity) : null,
                    };
                    await api.post('/owner/classes', data);
                }
            }
            setDialogOpen(false);
            fetchClasses();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta clase?')) return;
        try {
            await api.delete(`/owner/classes/${id}`);
            fetchClasses();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar.');
        }
    };

    const openCancel = (cls) => {
        setCancelClass(cls);
        setCancelMessage('');
        setCancelOpen(true);
    };

    const handleCancel = async (e) => {
        e.preventDefault();
        if (!cancelClass) return;
        setCancelling(true);
        try {
            await api.post(`/owner/classes/${cancelClass.id}/cancel`, { message: cancelMessage.trim() || undefined });
            setCancelOpen(false);
            fetchClasses();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al cancelar la clase.');
        } finally {
            setCancelling(false);
        }
    };

    const openAttendance = async (cls) => {
        setAttendanceClass(cls);
        setAttendanceOpen(true);
        setAttendanceLoading(true);
        try {
            const res = await api.get(`/owner/classes/${cls.id}/attendances`);
            setAttendances(res.data.data);
        } catch { setAttendances([]); }
        finally { setAttendanceLoading(false); }
    };

    const handleToggle = async (attendance) => {
        setTogglingId(attendance.id);
        try {
            const res = await api.post(`/owner/classes/${attendanceClass.id}/attendances/${attendance.id}/toggle`);
            setAttendances(prev => prev.map(a => a.id === attendance.id ? { ...a, presente: res.data.data.presente, attended_at: res.data.data.presente ? new Date().toISOString() : null } : a));
        } catch {}
        finally { setTogglingId(null); }
    };

    const openSongs = async (cls) => {
        setSongsClass(cls);
        setSongsOpen(true);
        setSongsLoading(true);
        try {
            const res = await api.get(`/owner/classes/${cls.id}/songs`);
            setSongs(res.data.data || []);
        } catch { setSongs([]); }
        finally { setSongsLoading(false); }
    };

    const formatSongsText = () => {
        if (!songsClass || songs.length === 0) return '';
        const header = `Canciones sugeridas — ${songsClass.name}`;
        const lines = songs.map((s, i) => {
            const linkLine = s.link ? `   Link: ${s.link}` : '';
            return `${i + 1}. ${s.title}${s.customer?.name ? ` (sugerida por ${s.customer.name})` : ''}${linkLine ? '\n' + linkLine : ''}`;
        });
        return [header, `${songs.length} cancione${songs.length !== 1 ? 's' : ''}`, '', ...lines].join('\n');
    };

    const handleCopySongs = async () => {
        const text = formatSongsText();
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setSongsCopied(true);
            setTimeout(() => setSongsCopied(false), 2000);
        } catch {
            // Fallback
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setSongsCopied(true);
            setTimeout(() => setSongsCopied(false), 2000);
        }
    };

    const handleDownloadSongs = () => {
        const text = formatSongsText();
        if (!text) return;
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `canciones-${songsClass?.name?.replace(/\s+/g, '-').toLowerCase() || 'clase'}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const canModify = (cls) => {
        if (cls.cancelled_at) return false;
        if (!cls.starts_at) return true;
        const diff = new Date(cls.starts_at) - new Date();
        return diff > 2 * 60 * 60 * 1000;
    };

    const canCancel = (cls) => {
        if (cls.cancelled_at) return false;
        if (!cls.starts_at || !cls.ends_at) return false;
        const durationMs = new Date(cls.ends_at) - new Date(cls.starts_at);
        const deadline = new Date(new Date(cls.starts_at).getTime() + durationMs / 2);
        return new Date() <= deadline;
    };

    const openGoals = async (cls) => {
        setGoalsClass(cls);
        setGoalsOpen(true);
        setGoalsLoading(true);
        setGoalError('');
        setEditingGoal(null);
        setGoalForm({ title: '', description: '', target_value: '', unit: '', points: '' });
        try {
            const res = await api.get(`/owner/classes/${cls.id}/goals`);
            setGoals(res.data.data || []);
        } catch { setGoals([]); }
        finally { setGoalsLoading(false); }
    };

    const handleSaveGoal = async (e) => {
        e.preventDefault();
        setGoalSaving(true);
        setGoalError('');
        try {
            const data = {
                title: goalForm.title,
                description: goalForm.description || undefined,
                target_value: parseFloat(goalForm.target_value),
                unit: goalForm.unit || undefined,
                points: parseInt(goalForm.points),
            };
            if (editingGoal) {
                await api.put(`/owner/classes/${goalsClass.id}/goals/${editingGoal.id}`, data);
            } else {
                await api.post(`/owner/classes/${goalsClass.id}/goals`, data);
            }
            setEditingGoal(null);
            setGoalForm({ title: '', description: '', target_value: '', unit: '', points: '' });
            const res = await api.get(`/owner/classes/${goalsClass.id}/goals`);
            setGoals(res.data.data || []);
        } catch (err) {
            setGoalError(err.response?.data?.message || 'Error al guardar el objetivo.');
        } finally { setGoalSaving(false); }
    };

    const handleEditGoal = (goal) => {
        setEditingGoal(goal);
        setGoalForm({
            title: goal.title,
            description: goal.description || '',
            target_value: String(goal.target_value),
            unit: goal.unit || '',
            points: String(goal.points),
        });
    };

    const handleDeleteGoal = async (goalId) => {
        if (!confirm('¿Estás seguro de eliminar este objetivo?')) return;
        try {
            await api.delete(`/owner/classes/${goalsClass.id}/goals/${goalId}`);
            const res = await api.get(`/owner/classes/${goalsClass.id}/goals`);
            setGoals(res.data.data || []);
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar.');
        }
    };

    const openRanking = async (cls) => {
        setRankingClass(cls);
        setRankingOpen(true);
        setRankingLoading(true);
        try {
            const res = await api.get(`/owner/classes/${cls.id}/ranking`);
            setRanking(res.data.data || []);
        } catch { setRanking([]); }
        finally { setRankingLoading(false); }
    };

    const fetchCopyPreview = useCallback(async () => {
        if (!copyForm.source_date) return;
        setCopyPreviewLoading(true);
        try {
            const res = await api.get('/owner/classes/copy-preview', {
                params: { source_date: copyForm.source_date, mode: copyForm.mode },
            });
            const classes = res.data.classes || [];
            setCopyPreviewClasses(classes);
            setCopySelectedIds(classes.map(c => c.id));
        } catch {
            setCopyPreviewClasses([]);
            setCopySelectedIds([]);
        } finally {
            setCopyPreviewLoading(false);
        }
    }, [copyForm.source_date, copyForm.mode]);

    useEffect(() => {
        if (copyOpen) {
            fetchCopyPreview();
        }
    }, [copyOpen, fetchCopyPreview]);

    const handleCopy = async (e) => {
        e.preventDefault();
        setCopyLoading(true);
        setCopyError('');
        try {
            const payload = { ...copyForm, selected_ids: copySelectedIds };
            const res = await api.post('/owner/classes/copy', payload);
            setCopyOpen(false);
            setCopyPreviewClasses([]);
            setCopySelectedIds([]);
            setCopyCreatedIds(res.data.created_ids || []);
            setCopySuccess(res.data.message || `Se crearon ${res.data.created} clases exitosamente.`);
            fetchClasses();
        } catch (err) {
            setCopyError(err.response?.data?.message || 'Error al copiar clases.');
        } finally { setCopyLoading(false); }
    };

    const handleUndo = async () => {
        if (copyCreatedIds.length === 0) return;
        setCopyUndoLoading(true);
        try {
            const res = await api.post('/owner/classes/copy-undo', { created_ids: copyCreatedIds });
            setCopySuccess(null);
            setCopyCreatedIds([]);
            alert(res.data.message);
            fetchClasses();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al deshacer.');
        } finally { setCopyUndoLoading(false); }
    };

    const getCopyPreviewLabel = () => {
        const modeLabel = copyForm.mode === 'week' ? 'semana' : 'mes';
        const copiesLabel = copyForm.copies === 1 ? '1 vez' : `${copyForm.copies} veces`;
        return `Copiar ${modeLabel} — ${copiesLabel}${copyForm.copy_goals ? ' (con objetivos)' : ''}`;
    };

    const toggleClassSelection = (id) => {
        setCopySelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const buildCopyPreviewList = () => {
        if (copyPreviewClasses.length === 0) return [];
        return copyPreviewClasses.map(cls => {
            const instances = [];
            for (let i = 1; i <= copyForm.copies; i++) {
                const sourceDate = new Date(cls.starts_at);
                const targetDate = copyForm.mode === 'week'
                    ? new Date(sourceDate.getTime() + i * 7 * 24 * 60 * 60 * 1000)
                    : new Date(sourceDate.getFullYear(), sourceDate.getMonth() + i, sourceDate.getDate(), sourceDate.getHours(), sourceDate.getMinutes());
                const timeStr = targetDate.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
                const dateStr = `${targetDate.getDate()}/${targetDate.getMonth() + 1}`;
                instances.push({ date: dateStr, time: timeStr, key: `${cls.id}-${i}` });
            }
            return { ...cls, instances };
        });
    };

    const goDatePrev = () => {
        if (!dateFilter) return;
        const d = new Date(dateFilter);
        d.setDate(d.getDate() - 1);
        setDateFilter(d.toISOString().slice(0, 10));
        setPage(1);
    };

    const goDateNext = () => {
        if (!dateFilter) return;
        const d = new Date(dateFilter);
        d.setDate(d.getDate() + 1);
        setDateFilter(d.toISOString().slice(0, 10));
        setPage(1);
    };

    const goDateToday = () => {
        setDateFilter(today);
        setPage(1);
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-wide text-white">Clases</h1>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => { setCopyForm({ source_date: today, mode: 'week', copies: 1, copy_goals: false }); setCopyError(''); setCopyOpen(true); }} variant="outline" className="w-full sm:w-auto border-[#3a3a3f] text-white hover:bg-white/5 text-xs sm:text-sm">
                        <Copy className="h-4 w-4 mr-2" />Copiar semana/mes
                    </Button>
                    <Button onClick={openCreate} className="w-full sm:w-auto text-xs sm:text-sm"><Plus className="h-4 w-4 mr-2" />Nueva Clase</Button>
                </div>
            </div>

            {copySuccess && (
                <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 rounded-lg border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-400">
                    <div className="flex items-center gap-2 flex-1">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        <span>{copySuccess}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {copyCreatedIds.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={handleUndo} disabled={copyUndoLoading} className="text-green-400 hover:text-green-300 hover:bg-green-500/20 h-7 px-2">
                                {copyUndoLoading ? 'Deshaciendo...' : 'Deshacer'}
                            </Button>
                        )}
                        <button onClick={() => setCopySuccess(null)} className="text-green-400 hover:text-green-300 px-1">
                            ×
                        </button>
                    </div>
                </div>
            )}

            <div className="flex border-b border-[#3a3a3f] mb-4">
                <button
                    onClick={() => setActiveTab('list')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${activeTab === 'list' ? 'border-white text-white' : 'border-transparent text-white/40 hover:text-white/60'}`}
                >
                    <List className="h-4 w-4" />Lista
                </button>
                <button
                    onClick={() => setActiveTab('calendar')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium uppercase tracking-wider border-b-2 transition-colors ${activeTab === 'calendar' ? 'border-white text-white' : 'border-transparent text-white/40 hover:text-white/60'}`}
                >
                    <CalendarDays className="h-4 w-4" />Calendario
                </button>
            </div>

            {activeTab === 'calendar' && (
                <ClassCalendar onClassClick={cls => openEdit(cls)} />
            )}

            {activeTab === 'list' && (
            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <div className="flex items-center gap-2 flex-1">
                            <Search className="h-4 w-4 text-white/60 shrink-0" />
                            <Input placeholder="Buscar clases..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="w-full sm:max-w-sm bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                                <Button variant="outline" size="sm" onClick={goDatePrev} className="border-[#3a3a3f] text-white hover:bg-white/5 px-2 h-9">
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={goDateToday} className="border-[#3a3a3f] text-white hover:bg-white/5 text-xs h-9">
                                    Hoy
                                </Button>
                                <Button variant="outline" size="sm" onClick={goDateNext} className="border-[#3a3a3f] text-white hover:bg-white/5 px-2 h-9">
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                            <Input type="date" value={dateFilter} onChange={e => { setDateFilter(e.target.value); setPage(1); }} className="w-full sm:w-auto sm:max-w-[160px] bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            {dateFilter && (
                                <Button variant="ghost" size="sm" onClick={() => { setDateFilter(''); setPage(1); }} className="text-white/60 hover:text-white shrink-0">
                                    Limpiar
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : classes.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron clases.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto -mx-2 px-2">
                                <table className="w-full text-sm min-w-[320px]">
                                    <thead>
                                        <tr className="border-b border-[#3a3a3f]">
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider text-xs sm:text-sm">Nombre</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider whitespace-nowrap text-xs sm:text-sm">Inicio</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider hidden sm:table-cell text-xs sm:text-sm">Cap.</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider hidden sm:table-cell text-xs sm:text-sm">Ins.</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider sm:hidden w-[55px] whitespace-nowrap text-xs sm:text-sm">Uso</th>
                                            <th className="text-right py-3 px-2 font-medium text-white/60 uppercase tracking-wider w-[48px] sm:w-auto whitespace-nowrap text-xs sm:text-sm">Acc.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {classes.map(cls => (
                                            <tr key={cls.id} className={`border-b border-[#3a3a3f] last:border-0 hover:bg-white/5 ${cls.cancelled_at ? 'opacity-60' : ''}`}>
                                                <td className="py-3 px-2 font-medium text-white text-xs sm:text-sm min-w-[100px]">
                                                    {cls.name}
                                                    {cls.cancelled_at && (
                                                        <span className="ml-1 sm:ml-2 inline-flex items-center px-1 py-0.5 rounded text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/30">
                                                            Cancelada
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2 text-white/60 whitespace-nowrap text-xs sm:text-sm">{formatTime(cls.starts_at)}</td>
                                                <td className="py-3 px-2 text-white/60 hidden sm:table-cell text-xs sm:text-sm">{cls.max_capacity || '-'}</td>
                                                <td className="py-3 px-2 text-white/60 hidden sm:table-cell text-xs sm:text-sm">{cls.attendances_count ?? '-'}</td>
                                                <td className="py-3 px-2 text-white/60 sm:hidden w-[55px] whitespace-nowrap text-xs sm:text-sm">{cls.attendances_count ?? 0}/{cls.max_capacity ?? '-'}</td>
                                                <td className="py-3 px-2 text-right w-[48px] sm:w-auto whitespace-nowrap">
                                                    {/* Desktop inline buttons */}
                                                    <div className="hidden sm:flex justify-end gap-1">
                                                        {!cls.cancelled_at && (
                                                            <>
                                                                <Button variant="ghost" size="icon" onClick={() => openAttendance(cls)} title="Tomar asistencia" className="text-white hover:bg-white/5">
                                                                    <ClipboardCheck className="h-4 w-4 text-white" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => openSongs(cls)} title="Ver canciones sugeridas" className="text-white hover:bg-white/5">
                                                                    <Music className="h-4 w-4 text-white" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => openGoals(cls)} title="Objetivos" className="text-white hover:bg-white/5">
                                                                    <Target className="h-4 w-4 text-white" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => openRanking(cls)} title="Ranking" className="text-white hover:bg-white/5">
                                                                    <Trophy className="h-4 w-4 text-white" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => openCancel(cls)} disabled={!canCancel(cls)} title={canCancel(cls) ? 'Cancelar clase' : 'Fuera del tiempo de cancelación'} className="text-white hover:bg-white/5">
                                                                    <Ban className={`h-4 w-4 ${canCancel(cls) ? 'text-red-400' : 'text-white/30'}`} />
                                                                </Button>
                                                            </>
                                                        )}
                                                        <Button variant="ghost" size="icon" onClick={() => openEdit(cls)} disabled={!canModify(cls)} title={!canModify(cls) ? 'No se puede modificar (menos de 2hs)' : ''} className="text-white hover:bg-white/5">
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(cls.id)} disabled={!canModify(cls)} className="text-white hover:bg-white/5">
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    {/* Mobile dropdown */}
                                                    <div className="sm:hidden">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="text-white hover:bg-white/5">
                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                {!cls.cancelled_at && (
                                                                    <>
                                                                        <DropdownMenuItem onClick={() => openAttendance(cls)} className="gap-2">
                                                                            <ClipboardCheck className="h-4 w-4" /> Tomar asistencia
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => openSongs(cls)} className="gap-2">
                                                                            <Music className="h-4 w-4" /> Canciones
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => openGoals(cls)} className="gap-2">
                                                                            <Target className="h-4 w-4" /> Objetivos
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => openRanking(cls)} className="gap-2">
                                                                            <Trophy className="h-4 w-4" /> Ranking
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => openCancel(cls)} disabled={!canCancel(cls)} className="gap-2 text-red-400 focus:text-red-400">
                                                                            <Ban className="h-4 w-4" /> Cancelar clase
                                                                        </DropdownMenuItem>
                                                                    </>
                                                                )}
                                                                <DropdownMenuItem onClick={() => openEdit(cls)} disabled={!canModify(cls)} className="gap-2">
                                                                    <Pencil className="h-4 w-4" /> Editar
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleDelete(cls.id)} disabled={!canModify(cls)} className="gap-2 text-red-400 focus:text-red-400">
                                                                    <Trash2 className="h-4 w-4" /> Eliminar
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex flex-col sm:flex-row justify-center items-center gap-2 mt-4">
                                    <div className="flex gap-2 w-full sm:w-auto">
                                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#3a3a3f] text-white hover:bg-white/5 flex-1 sm:flex-none">Anterior</Button>
                                        <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="border-[#3a3a3f] text-white hover:bg-white/5 flex-1 sm:flex-none">Siguiente</Button>
                                    </div>
                                    <span className="text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">{editing ? 'Editar Clase' : 'Nueva Clase'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Nombre</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Fecha</Label>
                                <Input type="date" min={today} value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Hora inicio</Label>
                                <Input type="time" placeholder="14:30" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Duración</Label>
                                <select className="flex h-9 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm text-white" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} required>
                                    {DURATIONS.map(d => <option key={d.value} value={d.value} className="bg-[#0a0a0a] text-white">{d.label}</option>)}
                                </select>
                            </div>
                        </div>
                        {form.start_date && form.start_time && form.duration && (
                            <p className="text-xs text-white/60">
                                Fin: {(() => { const e = calcEndDateTime(form.start_date, form.start_time, form.duration); return e ? formatDateTimeShort(e) : '-'; })()}
                            </p>
                        )}
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Capacidad máxima</Label>
                            <Input type="number" min="1" value={form.max_capacity} onChange={e => setForm({ ...form, max_capacity: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                        </div>
                        {!editing && form.start_date && (
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-white/60 uppercase tracking-wider text-xs"><Calendar className="h-4 w-4" />Copiar clase en los siguientes días</Label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {getNextDaysOptions().map(option => (
                                        <label
                                            key={option.value}
                                            className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                                form.copy_days.includes(option.value)
                                                    ? 'bg-white/5 border-white/20'
                                                    : 'bg-[#0a0a0a] border-transparent hover:bg-white/5'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.copy_days.includes(option.value)}
                                                onChange={() => toggleCopyDay(option.value)}
                                                className="h-4 w-4 rounded border-white text-white focus:ring-white"
                                            />
                                            <span className="text-sm text-white">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                                {form.copy_days.length > 0 && (
                                    <p className="text-xs text-white/60">
                                        Se crearán {form.copy_days.length + 1} clases individuales: 1 para la fecha seleccionada + {form.copy_days.length} copias
                                    </p>
                                )}
                            </div>
                        )}
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5 w-full sm:w-auto">Cancelar</Button>
                            <Button type="submit" disabled={saving} className="w-full sm:w-auto">{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            <Dialog open={attendanceOpen} onOpenChange={setAttendanceOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <ClipboardCheck className="h-5 w-5 text-white" />
                            Asistencia — {attendanceClass?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {attendanceLoading ? (
                        <p className="text-white/60 text-center py-6">Cargando...</p>
                    ) : attendances.length === 0 ? (
                        <p className="text-white/60 text-center py-6">No hay inscriptos en esta clase.</p>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {attendances.map(a => (
                                <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg border ${a.presente ? 'bg-white/5 border-[#3a3a3f]' : 'hover:bg-white/5 border-[#3a3a3f]'}`}>
                                    <div>
                                        <p className="font-medium text-sm text-white">{a.nombre}</p>
                                        <p className="text-xs text-white/60">DNI: {a.dni}</p>
                                    </div>
                                    <Button
                                        variant={a.presente ? 'default' : 'outline'}
                                        size="sm"
                                        disabled={togglingId === a.id}
                                        onClick={() => handleToggle(a)}
                                    >
                                        {a.presente ? (
                                            <><CheckCircle2 className="h-4 w-4 mr-1" />Presente</>
                                        ) : (
                                            <><Circle className="h-4 w-4 mr-1" />Ausente</>
                                        )}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <span className="text-sm text-white/60 order-2 sm:order-1">
                            {attendances.filter(a => a.presente).length}/{attendances.length} presentes
                        </span>
                        <Button variant="outline" onClick={() => setAttendanceOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5 w-full sm:w-auto order-1 sm:order-2">Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={songsOpen} onOpenChange={setSongsOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <Music className="h-5 w-5 text-white" />
                            Canciones sugeridas — {songsClass?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {songsLoading ? (
                        <p className="text-white/60 text-center py-6">Cargando...</p>
                    ) : songs.length === 0 ? (
                        <p className="text-white/60 text-center py-6">No hay canciones sugeridas para esta clase.</p>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {songs.map(s => (
                                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-[#3a3a3f] hover:bg-white/5">
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm text-white truncate">{s.title}</p>
                                        <div className="flex items-center gap-2 text-xs text-white/60 mt-0.5">
                                            <span>{s.customer?.name || 'Alumno'}</span>
                                            {s.link && (
                                                <a
                                                    href={s.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-300 flex items-center gap-0.5"
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                    Link
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mt-4">
                        <span className="text-sm text-white/60 shrink-0 text-center sm:text-left">
                            {songs.length} cancione{songs.length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
                            {songs.length > 0 && (
                                <>
                                    <Button variant="outline" size="sm" onClick={handleCopySongs} className="border-[#3a3a3f] text-white hover:bg-white/5 flex-1 sm:flex-none">
                                        {songsCopied ? <CheckCircle2 className="h-4 w-4 mr-1 text-green-400" /> : <Copy className="h-4 w-4 mr-1" />}
                                        {songsCopied ? 'Copiado' : 'Copiar'}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={handleDownloadSongs} className="border-[#3a3a3f] text-white hover:bg-white/5 flex-1 sm:flex-none">
                                        <Download className="h-4 w-4 mr-1" />
                                        Descargar
                                    </Button>
                                </>
                            )}
                            <Button variant="outline" size="sm" onClick={() => setSongsOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5 w-full sm:w-auto">Cerrar</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={goalsOpen} onOpenChange={setGoalsOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <Target className="h-5 w-5 text-white" />
                            Objetivos — {goalsClass?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {goalsLoading ? (
                        <p className="text-white/60 text-center py-6">Cargando...</p>
                    ) : (
                        <div className="space-y-4 max-h-[400px] overflow-y-auto">
                            {goals.length === 0 && !editingGoal && (
                                <p className="text-white/60 text-center py-4">No hay objetivos para esta clase.</p>
                            )}
                            {goals.map(g => (
                                <div key={g.id} className="flex items-center justify-between p-3 rounded-lg border border-[#3a3a3f] hover:bg-white/5">
                                    <div>
                                        <p className="font-medium text-sm text-white">{g.title}</p>
                                        <p className="text-xs text-white/60">
                                            Meta: {g.target_value} {g.unit || ''} — {g.points} pts
                                            {g.completions_count > 0 && (
                                                <span className="ml-2 text-white/40">({g.completions_count} registros)</span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => handleEditGoal(g)} className="text-white hover:bg-white/5 h-8 w-8">
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => handleDeleteGoal(g.id)} className="text-white hover:bg-white/5 h-8 w-8">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            <form onSubmit={handleSaveGoal} className="space-y-3 pt-2 border-t border-[#3a3a3f]">
                                {goalError && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{goalError}</div>}
                                <p className="text-xs text-white/60 uppercase tracking-wider">
                                    {editingGoal ? 'Editar objetivo' : 'Nuevo objetivo'}
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Título</Label>
                                        <Input value={goalForm.title} onChange={e => setGoalForm({ ...goalForm, title: e.target.value })} required placeholder="Ej: Burpees" className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Unidad</Label>
                                        <Input value={goalForm.unit} onChange={e => setGoalForm({ ...goalForm, unit: e.target.value })} placeholder="reps, min, kg..." className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Valor objetivo</Label>
                                        <Input type="number" min="0" step="0.01" value={goalForm.target_value} onChange={e => setGoalForm({ ...goalForm, target_value: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-white/60 uppercase tracking-wider text-xs">Puntos</Label>
                                        <Input type="number" min="1" value={goalForm.points} onChange={e => setGoalForm({ ...goalForm, points: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Descripción (opcional)</Label>
                                    <Input value={goalForm.description} onChange={e => setGoalForm({ ...goalForm, description: e.target.value })} className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                                </div>
                                <div className="flex gap-2">
                                    <Button type="submit" disabled={goalSaving} size="sm">{goalSaving ? 'Guardando...' : (editingGoal ? 'Guardar cambios' : 'Agregar')}</Button>
                                    {editingGoal && (
                                        <Button type="button" variant="outline" size="sm" onClick={() => { setEditingGoal(null); setGoalForm({ title: '', description: '', target_value: '', unit: '', points: '' }); setGoalError(''); }} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                                    )}
                                </div>
                            </form>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={rankingOpen} onOpenChange={setRankingOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <Trophy className="h-5 w-5 text-white" />
                            Ranking — {rankingClass?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {rankingLoading ? (
                        <p className="text-white/60 text-center py-6">Cargando...</p>
                    ) : ranking.length === 0 ? (
                        <p className="text-white/60 text-center py-6">Aún no hay puntos registrados para esta clase.</p>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {ranking.map((r, idx) => (
                                <div key={r.customer_id} className="flex items-center justify-between p-3 rounded-lg border border-[#3a3a3f] hover:bg-white/5">
                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm font-bold w-6 text-center ${idx < 3 ? 'text-yellow-400' : 'text-white/60'}`}>{idx + 1}</span>
                                        <p className="font-medium text-sm text-white">{r.nombre}</p>
                                    </div>
                                    <span className="text-sm font-bold text-white">{r.total_points} pts</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setRankingOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5 w-full sm:w-auto">Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <Ban className="h-5 w-5 text-red-400" />
                            Cancelar clase — {cancelClass?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCancel} className="space-y-4">
                        <p className="text-sm text-white/60">
                            Al cancelar la clase, se devolverá <strong className="text-white">un día</strong> a la tarjeta de todos los inscriptos. Esta acción no se puede deshacer.
                        </p>
                        <p className="text-xs text-white/40">
                            Solo se puede cancelar hasta la mitad del horario de la clase.
                        </p>
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Mensaje de cancelación (opcional)</Label>
                            <textarea
                                value={cancelMessage}
                                onChange={e => setCancelMessage(e.target.value)}
                                placeholder="Ej: La clase se cancela por mantenimiento del salón."
                                maxLength={500}
                                rows={3}
                                className="flex w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:ring-white"
                            />
                        </div>
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button type="button" variant="outline" onClick={() => setCancelOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5 w-full sm:w-auto">Cerrar</Button>
                            <Button type="submit" disabled={cancelling} className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto">
                                {cancelling ? 'Cancelando...' : 'Confirmar cancelación'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <Copy className="h-5 w-5 text-white" />
                            Copiar semana/mes
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCopy} className="space-y-4">
                        {copyError && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{copyError}</div>}
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Fecha de referencia</Label>
                            <Input type="date" value={copyForm.source_date} onChange={e => setCopyForm({ ...copyForm, source_date: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            <p className="text-xs text-white/40">Se usará la semana o mes que contenga esta fecha.</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Modo</Label>
                                <select className="flex h-9 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm text-white" value={copyForm.mode} onChange={e => setCopyForm({ ...copyForm, mode: e.target.value })} required>
                                    <option value="week" className="bg-[#0a0a0a] text-white">Semana</option>
                                    <option value="month" className="bg-[#0a0a0a] text-white">Mes</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Copias (1-4)</Label>
                                <Input type="number" min="1" max="4" value={copyForm.copies} onChange={e => setCopyForm({ ...copyForm, copies: parseInt(e.target.value) || 1 })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="copy_goals"
                                checked={copyForm.copy_goals}
                                onChange={e => setCopyForm({ ...copyForm, copy_goals: e.target.checked })}
                                className="h-4 w-4 rounded border-white text-white focus:ring-white"
                            />
                            <Label htmlFor="copy_goals" className="text-white/60 uppercase tracking-wider text-xs cursor-pointer">Incluir objetivos</Label>
                        </div>
                        <div className="space-y-2">
                            <p className="text-xs text-white/60 uppercase tracking-wider">{getCopyPreviewLabel()}</p>
                            {copyPreviewLoading ? (
                                <p className="text-xs text-white/40">Cargando vista previa...</p>
                            ) : copyPreviewClasses.length === 0 ? (
                                <p className="text-xs text-white/40">No hay clases en el período seleccionado.</p>
                            ) : (
                                <div className="max-h-[240px] overflow-y-auto space-y-2 rounded-lg border border-[#3a3a3f] bg-white/5 p-3">
                                    {buildCopyPreviewList().map(cls => (
                                        <div key={cls.id}>
                                            <label className="flex items-start gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={copySelectedIds.includes(cls.id)}
                                                    onChange={() => toggleClassSelection(cls.id)}
                                                    className="h-4 w-4 rounded border-white text-white focus:ring-white shrink-0 mt-0.5"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <span className="text-sm font-medium text-white truncate max-w-[120px] sm:max-w-[200px] md:max-w-none inline-block align-middle">{cls.name}</span>
                                                    {copySelectedIds.includes(cls.id) && (
                                                        <span className="text-xs text-white/60 ml-1 align-middle">
                                                            {cls.instances.map((inst, idx) => (
                                                                <span key={inst.key}>{idx > 0 ? ', ' : ''}{inst.date} {inst.time}</span>
                                                            ))}
                                                        </span>
                                                    )}
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button type="button" variant="outline" onClick={() => setCopyOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5 w-full sm:w-auto">Cancelar</Button>
                            <Button type="submit" disabled={copyLoading} className="w-full sm:w-auto">{copyLoading ? 'Copiando...' : 'Copiar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
